import {
    useAddBucketImageDocTypeMapping,
    useBucketImageDocTypeMapping,
    useDeleteBucketImageDocTypeMapping
} from "./queries";
import {Button} from "reactstrap";
import {FaPlus} from "react-icons/fa";
import {BucketConfigForm, BucketConfigFormModal, BucketConfigTable} from "./components";
import {useBoolean} from "usehooks-ts";
import {extractAccessToken} from "../../helpers/jwt";
import Swal from "sweetalert2";
import {useQueryClient} from "@tanstack/react-query";
import axios, {AxiosError} from "axios";
import {BucketImageDocType} from "../../models/config/BucketImageDocType";

const getErrorMessage = (error: unknown) =>
    axios.isAxiosError(error)
        ? (error as AxiosError<{ message?: string }>).response?.data.message
        : error instanceof Error
            ? error.message
            : 'Unknown error occurred.';

export const LegacyDMSBucketConfig = () => {
    const { data: bucketConfigs, isFetching: isFetchingBucketConfigs } = useBucketImageDocTypeMapping();
    
    const queryClient = useQueryClient();

    const { value: isOpenCreateModal, toggle: toggleIsOpenCreateModal } =
        useBoolean();
    const { mutate: addBucketImageDocTypeMapping } = useAddBucketImageDocTypeMapping();
    
    const { mutate: deleteBucketImageDocTypeMapping } = useDeleteBucketImageDocTypeMapping();
    
    const handleSubmitAddBucketImageDocTypeMapping = (bucketImageDocType: BucketImageDocType) => {
        const request = {
            ...bucketImageDocType,
            created_by:
                extractAccessToken().citizen_id || extractAccessToken().username
        }

        addBucketImageDocTypeMapping(request, {
            onSuccess: async () => {
                await Swal.fire({
                    title: 'Success!',
                    icon: 'success'
                });
            },
            onError: async error => {
                await Swal.fire({
                    title: 'Error!',
                    icon: 'error',
                    text: getErrorMessage(error),
                });
            },
            onSettled: () => {
                toggleIsOpenCreateModal();

                queryClient.invalidateQueries({
                    queryKey: ['bucket-image-doc-type-mapping']
                });
            }
        });
    };

    const handleDeleteBucketImageDocTypeMapping = async (bucketImageDocType: BucketImageDocType) => {
        const result = await Swal.fire({
            title: `ยืนยันการลบ ${bucketImageDocType?.image_doc_type}?`,
            icon: 'warning',
            showDenyButton: true,
            denyButtonText: 'ยกเลิก',
            confirmButtonText: 'ยืนยัน'
        });
        if (!result.isConfirmed) {
            return;
        }
        
        deleteBucketImageDocTypeMapping(bucketImageDocType, {
            onSuccess: async () => {
                await Swal.fire({
                    title: 'Success!',
                    icon: 'success'
                });
            },
            onError: async error => {
                await Swal.fire({
                    title: 'Error!',
                    icon: 'error',
                    text: getErrorMessage(error),
                });
            },
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: ['bucket-image-doc-type-mapping']
                });
            }
        });
    };
    
    return (
        <div className="master-data-content">
            <div className="tw-flex tw-justify-between">
                <div className="trapezoid-head tw-w-[200px]">
                    <span>DMB Bucket Config</span>
                </div>

                <Button
                    size="sm"
                    color="primary"
                    onClick={toggleIsOpenCreateModal}
                    className="tw-flex tw-items-center tw-gap-1 tw-self-center"
                >
                    <FaPlus />
                    <span>Create</span>
                </Button>
            </div>

            <div className="mat-box">
                <BucketConfigTable
                    isPending={isFetchingBucketConfigs}
                    bucketConfigs={bucketConfigs}
                    onDelete={handleDeleteBucketImageDocTypeMapping}
                />
                
                <BucketConfigFormModal
                    title="Create Bucket Config"
                    isOpen={isOpenCreateModal}
                    onClose={toggleIsOpenCreateModal}
                    onSubmit={handleSubmitAddBucketImageDocTypeMapping}
                />
            </div>
        </div>
    );
};